import {
  Box,
  Grid2 as Grid,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import { AdminBusinessDetail } from '../../../types/admin';

interface BusinessAnalyticsTabProps {
  business: AdminBusinessDetail;
}

/**
 * BusinessAnalyticsTab - Business performance analytics and charts
 *
 * Features:
 * - Revenue trend chart (6 months)
 * - Performance metrics overview
 * - Top performers list
 * - Client growth metrics
 * - Conversion funnel
 */
export default function BusinessAnalyticsTab({ business }: BusinessAnalyticsTabProps) {
  const formatCurrencyFull = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Extract top performers for chart
  const topPerformersData = business.analytics.agents.top_performers.slice(0, 5);

  // Calculate growth rate
  const revenueHistory = business.analytics.revenue.history;
  const currentRevenue = revenueHistory[revenueHistory.length - 1]?.revenue || 0;
  const previousRevenue = revenueHistory[revenueHistory.length - 2]?.revenue || 0;
  const revenueGrowthRate = previousRevenue > 0
    ? ((currentRevenue - previousRevenue) / previousRevenue) * 100
    : 0;

  // Calculate revenue per agent
  const revenuePerAgent = business.agents_count > 0
    ? business.analytics.revenue.total / business.agents_count
    : 0;

  return (
    <Box>
      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Revenue */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachMoneyIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Total Revenue (6mo)
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {formatCurrencyFull(business.analytics.revenue.total)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              {revenueGrowthRate >= 0 ? (
                <TrendingUpIcon fontSize="small" color="success" />
              ) : (
                <TrendingDownIcon fontSize="small" color="error" />
              )}
              <Typography
                variant="body2"
                color={revenueGrowthRate >= 0 ? 'success.main' : 'error.main'}
              >
                {Math.abs(revenueGrowthRate).toFixed(1)}% vs last month
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Average Revenue per Agent */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Avg Revenue/Agent
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {formatCurrencyFull(revenuePerAgent)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Across {business.agents_count} agents
            </Typography>
          </Paper>
        </Grid>

        {/* Total Clients */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PeopleIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Total Clients
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {business.analytics.clients.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {business.analytics.clients.new_this_month} new this month
            </Typography>
          </Paper>
        </Grid>

        {/* Active Clients */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StarIcon color="warning" />
              <Typography variant="body2" color="text.secondary">
                Active Clients
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {business.analytics.clients.active}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {((business.analytics.clients.active / business.analytics.clients.total) * 100).toFixed(0)}% of total
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Revenue History Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Revenue Trend (Last 6 Months)
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <LineChart
          xAxis={[
            {
              data: business.analytics.revenue.history.map((item) => item.month),
              scaleType: 'point',
              label: 'Month',
            },
          ]}
          series={[
            {
              data: business.analytics.revenue.history.map((item) => item.revenue),
              label: 'Revenue',
              color: '#1976d2',
              curve: 'monotoneX',
            },
          ]}
          height={300}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'middle' },
              padding: 0,
            },
          }}
        />
      </Paper>

      {/* Top Performers Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StarIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Top Performing Agents
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <BarChart
          xAxis={[
            {
              data: topPerformersData.map((item) => item.name),
              scaleType: 'band',
              label: 'Agent',
            },
          ]}
          series={[
            {
              data: topPerformersData.map((item) => item.revenue),
              label: 'Revenue',
              color: '#1976d2',
            },
          ]}
          height={300}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'middle' },
              padding: 0,
            },
          }}
        />
      </Paper>

      {/* Client Metrics */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Client Metrics
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              New This Month
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.clients.new_this_month}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Active Clients
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.clients.active}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Churn Rate
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.clients.churn_rate}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
